import {ProductBrandBadge, getProductBrandLogo, type BrandLogo} from './ProductBrandBadge'
import Image from 'next/image'
import {productCardImageFit} from './productCardImageFit'
import {productOnCampaign} from '@/lib/campaignProducts'
import Link from 'next/link'
import {ChevronLink} from '@/components/shared/ChevronLink'
import {EnergyClassArrow} from '@/components/shared/EnergyClassArrow'
import type {ReactNode} from 'react'
import type {ProductDocument} from '@/types/sanity'
import {
  productDescription,
  productFromPriceForMarket,
  productHeroImageUrl,
  productSeries,
  productTitle,
  productVariantSwatchColor,
} from '@/components/products/productDisplay'
import {ColorSwatches} from '@/components/shared/ColorSwatches'
import {formatPrice} from '@/lib/formatting'
import {t} from '@/lib/i18n'
import {cn} from '@/lib/utils'
import {Badge} from '@/components/ui/badge'

type ProductCardProps = {
  product: ProductDocument
  href?: string | null
  locale?: string
  ctaLabel?: string
  ctaVariant?: 'button' | 'text'
  secondaryCta?: ReactNode
  priceAccessory?: ReactNode
  ctaLayout?: 'row' | 'column'
  className?: string
  showHighlights?: boolean
  statusLabel?: string
  deemphasized?: boolean
  equalizeContentHeight?: boolean
  marketKey?: string
  hideProductPrices?: boolean
  showDescription?: boolean
  priority?: boolean
}

export function ProductCard({
  product,
  href = '#',
  locale,
  ctaLabel,
  ctaVariant,
  secondaryCta,
  priceAccessory,
  ctaLayout,
  className,
  statusLabel,
  deemphasized = false,
  equalizeContentHeight = false,
  marketKey,
  hideProductPrices = false,
  showHighlights = true,
  showDescription = true,
  priority = false,
}: ProductCardProps) {
  const imageUrl = productHeroImageUrl(product)
  const series = productSeries(product)
  const title = productTitle(product)
  const brandLabel = product.brand?.title || product.brandKey || series
  const brandLogo = getProductBrandLogo(product.brandKey, product.brand?.title)
  const description = productDescription(product, locale)
  const fromPrice = hideProductPrices ? null : productFromPriceForMarket(product, marketKey)
  // JO2-247: the same rule as the "On Campaign" listing filter.
  const onCampaign = productOnCampaign(product, marketKey, hideProductPrices)
  const productHref = href || ''
  const hasProductLink = Boolean(productHref)
  const colors = (product.variants || [])
    .filter((v) => v.colorHex)
    .map((v) => ({
      hex: productVariantSwatchColor(v.colorHex, v.color) || v.colorHex || '#ccc',
      label: v.color || '',
    }))

  return (
    <article className={cn('group h-full u-card flex flex-col', className)}>
      {hasProductLink ? (
        <Link
          href={productHref}
          prefetch={false}
          className="block flex-1"
          aria-label={title}
          data-analytics-cta-name="product_card_cta"
          data-analytics-cta-location="product_card"
          data-analytics-cta-text={title}
        >
          <ProductCardContent
            imageUrl={imageUrl}
            title={title}
            brandLogo={brandLogo}
            brandLabel={brandLabel}
            description={description}
            statusLabel={statusLabel}
            deemphasized={deemphasized}
            equalizeContentHeight={equalizeContentHeight}
            showDescription={showDescription}
            showHighlights={showHighlights}
            fromPrice={fromPrice}
            priceAccessory={priceAccessory}
            colors={colors}
            ecoLabel={product.ecoLabel}
            onCampaign={onCampaign}
            locale={locale}
            priority={priority}
          />
        </Link>
      ) : (
        <div className="block flex-1">
          <ProductCardContent
            imageUrl={imageUrl}
            title={title}
            brandLogo={brandLogo}
            brandLabel={brandLabel}
            description={description}
            statusLabel={statusLabel}
            deemphasized={deemphasized}
            equalizeContentHeight={equalizeContentHeight}
            showDescription={showDescription}
            showHighlights={showHighlights}
            fromPrice={fromPrice}
            priceAccessory={priceAccessory}
            colors={colors}
            ecoLabel={product.ecoLabel}
            onCampaign={onCampaign}
            locale={locale}
            priority={priority}
          />
        </div>
      )}
      {/* Secondary CTA / custom actions */}
      {(secondaryCta || (ctaLabel && hasProductLink)) && (
        <div
          className={cn(
            'border-t border-border px-5 pb-5 pt-3',
            ctaLayout === 'column'
              ? 'flex flex-col items-center gap-2'
              : 'flex items-center justify-between gap-3',
          )}
        >
          {secondaryCta}
          {ctaLabel && hasProductLink ? (
            <Link href={productHref} prefetch={false}>
              <ChevronLink
                className={cn(
                  ctaVariant === 'text'
                    ? 'u-body-sm font-semibold text-foreground underline-offset-2 hover:underline'
                    : 'justify-center rounded-full border border-border px-4 py-2 u-body-sm font-semibold text-foreground hover:bg-muted',
                )}
              >
                {ctaLabel.replace(/\s*>\s*$/, '')}
              </ChevronLink>
            </Link>
          ) : null}
        </div>
      )}
    </article>
  )
}

function ProductCardContent({
  imageUrl,
  title,
  brandLogo,
  brandLabel,
  description,
  statusLabel,
  deemphasized,
  equalizeContentHeight,
  showDescription,
  showHighlights,
  fromPrice,
  priceAccessory,
  colors,
  ecoLabel,
  onCampaign,
  locale,
  priority,
}: {
  imageUrl?: string | null
  title: string
  brandLogo: BrandLogo
  brandLabel: string
  description?: string
  statusLabel?: string
  deemphasized: boolean
  equalizeContentHeight: boolean
  showDescription: boolean
  showHighlights: boolean
  fromPrice?: {amount: number; originalAmount?: number; currency?: string} | null
  priceAccessory?: ReactNode
  colors: Array<{hex: string; label: string}>
  ecoLabel?: string | null
  onCampaign: boolean
  locale?: string
  priority: boolean
}) {
  return (
    <>
      {/* Image — square media well */}
      <div className="relative aspect-square overflow-hidden bg-neutral-100">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt=""
            fill
            sizes="(min-width: 1280px) 33vw, (min-width: 640px) 50vw, 100vw"
            priority={priority}
            loading={priority ? 'eager' : 'lazy'}
            // Explicit hint complements `priority` — Next.js 16 doesn't
            // always forward `priority` to the rendered `<img>` as
            // `fetchpriority=high`. Without this the first-row product cards
            // (the LCP candidates on category pages like
            // /products/wood-stoves) don't get the browser preload hint,
            // which costs 1-3s on slow connections. Same workaround
            // HeroBackground.tsx already applies.
            fetchPriority={priority ? 'high' : 'auto'}
            className={cn(
              // Near-square assets fill the well; other ratios retain breathing room.
              productCardImageFit(imageUrl),
              'transition-transform duration-300 group-hover:scale-105',
              deemphasized && 'opacity-35',
            )}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-muted">
            <span className="u-body-sm text-muted-foreground">
              {t(locale, 'product.noImage', 'No image')}
            </span>
          </div>
        )}
        {statusLabel ? (
          <div className="absolute left-1/2 top-1/2 w-[86%] -translate-x-1/2 -translate-y-1/2 border border-neutral-500 bg-white/90 px-3 py-2 text-center text-xs font-semibold uppercase tracking-[0.08em] text-neutral-700">
            {statusLabel}
          </div>
        ) : null}
        {/* JO2-196: square badges with visually centered brand artwork.
            Shared with the larger product-detail badge. */}
        <div className="absolute left-4 top-4">
          <ProductBrandBadge logo={brandLogo} label={brandLabel} />
        </div>
        {/* JO2-247: the shared Badge (default variant), top right, opposite
            the brand badge. Label translated per market. */}
        {onCampaign ? (
          <Badge className="absolute right-4 top-4 uppercase tracking-[0.06em]">
            {t(locale, 'product.campaignBadge', 'Campaign')}
          </Badge>
        ) : null}
      </div>

      {/* Info area — white with rounded bottom-right */}
      <div
        className={cn(
          'flex flex-1 flex-col gap-3 rounded-br-card bg-white px-5 pb-5 pt-4',
          equalizeContentHeight && 'h-[250px]',
        )}
      >
        {/* Article number intentionally hidden (client request, JO2 2026-07): the imported
            productNumber is the fictive BASE/mother PIM number, not the actual item, which
            confused dealers. Re-enable here (or add a CMS visibility toggle) if that changes. */}

        {/* Title */}
        <h2
          className={cn(
            'u-body-md font-semibold text-foreground',
            equalizeContentHeight ? 'line-clamp-2 min-h-[48px]' : '',
          )}
        >
          {title}
        </h2>

        {/* Description */}
        {description && showDescription ? (
          <p
            className={cn(
              'line-clamp-2 u-body-sm text-muted-foreground',
              equalizeContentHeight && 'min-h-[42px]',
            )}
          >
            {description}
          </p>
        ) : null}
        {(!description || !showDescription) && equalizeContentHeight ? (
          <p className="invisible min-h-[42px] u-body-sm" aria-hidden="true">
            placeholder
          </p>
        ) : null}

        {/* Price above variants + energy class */}
        {showHighlights ? (
          <div className={cn('mt-auto space-y-2 pt-1', equalizeContentHeight && 'min-h-[56px]')}>
            {fromPrice ? (
              <p className="u-body-sm text-foreground">
                {t(locale, 'product.priceFromPrefix', 'From')}{' '}
                <span className="font-semibold">
                  {formatPrice(fromPrice.amount)} {fromPrice.currency || 'NOK'}
                </span>
                {fromPrice.originalAmount ? (
                  <span className="ml-2 text-muted-foreground line-through">
                    {formatPrice(fromPrice.originalAmount)} {fromPrice.currency || 'NOK'}
                  </span>
                ) : null}
              </p>
            ) : null}
            {fromPrice?.originalAmount && priceAccessory ? (
              <div className="u-body-xs text-muted-foreground [margin-top:2px]">
                {priceAccessory}
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <ColorSwatches colors={colors} locale={locale} />
              {ecoLabel ? (
                <EnergyClassArrow
                  ecoLabel={ecoLabel}
                  className="ml-1 inline-flex origin-left scale-[0.88]"
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}
